import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  archiveCustomer,
  deleteCustomerUser,
  getCustomerBySlug,
  listCustomerMembers,
  updateCustomer,
  type CustomerMemberOut,
} from '@/api/customers';
import { ApiError } from '@/api/client';
import { formatApiError } from '@/api/errors';
import { createEngagement, listEngagementsForCustomer } from '@/api/engagements';
import { createInvitation } from '@/api/invitations';
import { CustomerOnboardingOverview } from '@/components/CustomerOnboardingOverview';
import { GlAccountWorkbench } from '@/components/GlAccountWorkbench';
import { InvoiceWorkbench } from '@/components/InvoiceWorkbench';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorCard } from '@/components/ui/error-card';
import { ProgressBar } from '@/components/ui/progress';
import { formatDate } from '@/lib/format';
import { useAuth } from '../auth/AuthContext';
import { useCurrentRole } from '../auth/useCurrentRole';
import type { EngagementOut, EngagementStatus, ProvinceCode } from '@/api/types';

const PROVINCE_CODES: readonly ProvinceCode[] = [
  'BC', 'AB', 'SK', 'MB', 'ON', 'QC', 'NB', 'NS', 'PE', 'NL', 'YT', 'NT', 'NU',
];

function toLocalIso(d: Date): string {
  // Local YYYY-MM-DD — not toISOString() because that's UTC, and a user
  // in late-evening PST would otherwise see tomorrow's date pre-filled.
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayIso(): string {
  return toLocalIso(new Date());
}

function fourYearsAgoIso(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 4);
  return toLocalIso(d);
}

export function CustomerDetailPage() {
  const { slug } = useParams({ from: '/customers/$slug' });
  // Deep-link params from finding cards / run banners:
  // ?tab=accounts&account=<gl account name> opens the workbench drawer.
  const search = useSearch({ strict: false }) as { tab?: string; account?: string };
  const navigate = useNavigate();
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const role = useCurrentRole();
  const [editing, setEditing] = useState(false);

  const customer = useQuery({
    queryKey: ['customers', slug, userKey],
    enabled: isSignedIn,
    queryFn: async () => getCustomerBySlug(slug, (await getIdToken() ?? '')),
  });

  const engagements = useQuery({
    queryKey: ['engagements', 'by-customer', slug, userKey],
    enabled: isSignedIn,
    queryFn: async () => listEngagementsForCustomer(slug, (await getIdToken() ?? '')),
  });

  if (!isSignedIn) {
    return (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Not signed in</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (customer.isPending) return <p className="text-brand-slate">Loading…</p>;
  if (customer.isError) return <ErrorCard title="Customer not found" error={customer.error} />;

  const a = customer.data;
  const isPractitioner = role.isPractitioner;
  const canCreate = isPractitioner;
  const canArchive = isPractitioner;

  return (
    <div className="grid gap-6">
      <header>
        <Breadcrumbs>
          <Link to="/customers" className="text-brand-royal underline-offset-4 hover:underline">
            Customers
          </Link>
          <span className="text-brand-navy">{a.name}</span>
        </Breadcrumbs>
        <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-brand-navy">{a.name}</h2>
            <p className="mt-1 font-mono text-xs text-brand-slate">{a.slug}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isPractitioner && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEditing((v) => !v)}
              >
                {editing ? 'Close editor' : 'Edit customer'}
              </Button>
            )}
            {isPractitioner && (
              <Button asChild type="button" size="sm" variant="outline">
                <Link to="/customers/$slug/preview" params={{ slug: a.slug }}>
                  Preview portal
                </Link>
              </Button>
            )}
            {canArchive && <ArchiveCustomerButton slug={a.slug} />}
          </div>
        </div>
      </header>

      {editing && isPractitioner && (
        <EditCustomerCard
          slug={a.slug}
          currentName={a.name}
          onClose={() => setEditing(false)}
        />
      )}

      <DetailTabs
        overview={
          isPractitioner ? (
            <div className="grid gap-6">
              <CustomerOnboardingOverview engagements={engagements.data?.items ?? []} />
              <section className="grid gap-3">
                <h3 className="text-base font-semibold text-brand-navy">Customer access</h3>
                <InviteCustomerCard slug={slug} />
                <CustomerMembersTable slug={slug} />
              </section>
            </div>
          ) : null
        }
        engagements={
          <section className="grid gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-base font-semibold text-brand-navy">Engagements</h3>
              {canCreate && <CreateEngagementTrigger slug={slug} />}
            </div>
            {engagements.isPending ? (
              <p className="text-brand-slate">Loading engagements…</p>
            ) : engagements.isError ? (
              <ErrorCard title="Couldn't load engagements" error={engagements.error} />
            ) : engagements.data.items.length === 0 ? (
              <Card className="max-w-xl">
                <CardHeader>
                  <CardTitle>No engagements yet</CardTitle>
                  <CardDescription>
                    {canCreate
                      ? 'Use “Create engagement” above to start one.'
                      : 'A practitioner will create one for you.'}
                  </CardDescription>
                </CardHeader>
              </Card>
            ) : (
              <EngagementsTable engagements={engagements.data.items} />
            )}
          </section>
        }
        accounts={
          <GlAccountWorkbench
            slug={slug}
            initialAccount={search.account}
            onInitialAccountConsumed={() =>
              navigate({
                to: '/customers/$slug',
                params: { slug },
                search: { tab: 'accounts' },
                replace: true,
              })
            }
          />
        }
        invoices={isPractitioner ? <InvoiceWorkbench slug={slug} /> : null}
        showAccountsTab={isPractitioner}
        initialTab={
          search.tab === 'accounts' && isPractitioner
            ? 'accounts'
            : search.tab === 'invoices' && isPractitioner
              ? 'invoices'
              : search.tab === 'engagements'
                ? 'engagements'
                : undefined
        }
      />
    </div>
  );
}

type DetailTab = 'overview' | 'engagements' | 'accounts' | 'invoices';

/** Tabbed layout for the customer detail page. "Accounts" is the GL
 *  chart-of-accounts classification workbench; "Invoices" is the
 *  flashcard invoice-assignment workbench — both practitioner-only,
 *  curated per customer. */
function DetailTabs({
  overview,
  engagements,
  accounts,
  invoices,
  showAccountsTab,
  initialTab,
}: {
  overview: ReactNode;
  engagements: ReactNode;
  accounts: ReactNode;
  invoices: ReactNode;
  showAccountsTab: boolean;
  initialTab?: DetailTab;
}) {
  const [tab, setTab] = useState<DetailTab>(
    initialTab ?? (overview ? 'overview' : 'engagements'),
  );
  // A ?tab= deep link can arrive while the page is already mounted
  // (e.g. an in-app link to the Invoices tab) — useState only reads
  // initialTab once, so sync subsequent changes explicitly.
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);
  const tabs: Array<{ id: DetailTab; label: string; hidden?: boolean }> = [
    { id: 'overview', label: 'Overview', hidden: !overview },
    { id: 'engagements', label: 'Engagements' },
    { id: 'accounts', label: 'Accounts', hidden: !showAccountsTab },
    { id: 'invoices', label: 'Invoices', hidden: !invoices },
  ];
  return (
    <div className="grid gap-4">
      <div role="tablist" className="flex gap-1 border-b border-border">
        {tabs
          .filter((t) => !t.hidden)
          .map((t) => (
            <button
              key={t.id}
              role="tab"
              type="button"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={
                tab === t.id
                  ? 'border-b-2 border-brand-royal px-3 py-2 text-sm font-semibold text-brand-navy'
                  : 'border-b-2 border-transparent px-3 py-2 text-sm text-brand-slate hover:text-brand-navy'
              }
            >
              {t.label}
            </button>
          ))}
      </div>
      {tab === 'overview' && overview}
      {tab === 'engagements' && engagements}
      {tab === 'accounts' && accounts}
      {tab === 'invoices' && invoices}
    </div>
  );
}

function EngagementsTable({ engagements }: { engagements: EngagementOut[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Period</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Onboarding</TableHead>
          <TableHead>Province</TableHead>
          <TableHead className="text-right">Audits</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {engagements.map((e) => (
          <EngagementsTableRow key={e.id} engagement={e} />
        ))}
      </TableBody>
    </Table>
  );
}

function EngagementsTableRow({ engagement: e }: { engagement: EngagementOut }) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to="/engagements/$id"
          params={{ id: e.id }}
          className="font-medium text-brand-navy underline-offset-4 hover:underline"
        >
          {e.name}
        </Link>
      </TableCell>
      <TableCell className="font-mono text-xs text-brand-slate">
        {formatDate(e.period_start)} → {formatDate(e.period_end)}
      </TableCell>
      <TableCell>
        <EngagementStatusBadge status={e.status} />
      </TableCell>
      <TableCell>
        {e.onboarding ? (
          <ProgressBar done={e.onboarding.done} total={e.onboarding.total} />
        ) : (
          <span className="text-xs text-brand-slate">—</span>
        )}
      </TableCell>
      <TableCell className="font-mono text-xs text-brand-slate">
        {e.default_province ?? '—'}
      </TableCell>
      <TableCell className="text-right font-mono">{e.audit_run_count}</TableCell>
    </TableRow>
  );
}

const STATUS_LABEL: Record<EngagementStatus, string> = {
  mnda_pending: 'MNDA pending',
  intake_pending: 'intake pending',
  engine_ready: 'engine ready',
  analyzing: 'analyzing',
  in_review: 'in review',
  evidence_pending: 'evidence pending',
  publishable: 'publishable',
  published: 'published',
};

function EngagementStatusBadge({ status }: { status: EngagementStatus }) {
  // Three colors mirror the rolled-up customer-status palette: blue =
  // pre-review (work queued), amber = mid-review, green = post-review.
  if (
    status === 'mnda_pending' ||
    status === 'intake_pending' ||
    status === 'engine_ready' ||
    status === 'analyzing'
  ) {
    return (
      <Badge className="border-transparent bg-brand-royal text-white hover:bg-brand-royal/90">
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  if (status === 'in_review' || status === 'evidence_pending') {
    return (
      <Badge className="border-transparent bg-amber-500 text-white hover:bg-amber-500/90">
        {STATUS_LABEL[status]}
      </Badge>
    );
  }
  return (
    <Badge className="border-transparent bg-emerald-600 text-white hover:bg-emerald-600/90">
      {STATUS_LABEL[status]}
    </Badge>
  );
}

function CreateEngagementTrigger({ slug }: { slug: string }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        Create engagement
      </Button>
    );
  }
  return <CreateEngagementForm slug={slug} onClose={() => setOpen(false)} />;
}

function CreateEngagementForm({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState(fourYearsAgoIso);
  const [periodEnd, setPeriodEnd] = useState(todayIso);
  const [province, setProvince] = useState<ProvinceCode | ''>('');

  const create = useMutation({
    mutationFn: async () =>
      createEngagement(
        slug,
        {
          name: name.trim(),
          period_start: periodStart,
          period_end: periodEnd,
          default_province: province === '' ? undefined : province,
        },
        (await getIdToken() ?? ''),
      ),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['engagements', 'by-customer', slug, userKey] });
      void navigate({ to: '/engagements/$id', params: { id: created.id } });
    },
  });

  const submittable =
    name.trim().length > 0 && periodStart !== '' && periodEnd !== '';

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>New engagement</CardTitle>
        <CardDescription>
          One audit period per engagement. You can run multiple audits inside it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!submittable) return;
            create.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="2026 Q1 GST/HST review"
              maxLength={256}
              required
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-brand-slate">Period start</span>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                required
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span className="text-brand-slate">Period end</span>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                required
                className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
          </div>
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">
              Default province <span className="text-xs">(optional — editable later)</span>
            </span>
            <select
              value={province}
              onChange={(e) => setProvince(e.target.value as ProvinceCode | '')}
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            >
              <option value="">— none —</option>
              {PROVINCE_CODES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={create.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            {create.isError && (
              <span className="text-xs text-destructive">
                {formatApiError(create.error, 'Create failed')}
              </span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function EditCustomerCard({
  slug,
  currentName,
  onClose,
}: {
  slug: string;
  currentName: string;
  onClose: () => void;
}) {
  const { getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(currentName);
  const [newSlug, setNewSlug] = useState(slug);

  const save = useMutation({
    mutationFn: async () =>
      updateCustomer(
        slug,
        { name: name.trim(), slug: newSlug.trim() },
        (await getIdToken() ?? ''),
      ),
    onSuccess: (updated) => {
      onClose();
      if (updated.slug !== slug) {
        // The URL handle moved — navigate before invalidating, or the
        // still-mounted old-slug query refetches, 404s, and flashes the
        // "Customer not found" card until the navigation commits.
        void navigate({ to: '/customers/$slug', params: { slug: updated.slug } }).then(() => {
          void qc.invalidateQueries({ queryKey: ['customers'] });
        });
        return;
      }
      void qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const submittable =
    name.trim().length > 0 &&
    newSlug.trim().length > 0 &&
    !save.isPending &&
    (name.trim() !== currentName || newSlug.trim() !== slug);

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Edit customer</CardTitle>
        <CardDescription>
          Rename a placeholder once the real customer name is known. Changing the slug
          moves the customer&apos;s URL; if the customer has already been invited, the
          new name is synced to their sign-in organization automatically.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!submittable) return;
            save.mutate();
          }}
        >
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">Customer name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={128}
              required
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-brand-slate">
              Slug <span className="text-xs">(changes the customer&apos;s URL)</span>
            </span>
            <input
              type="text"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value.toLowerCase())}
              maxLength={64}
              pattern="^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$"
              required
              className="rounded-md border border-input bg-transparent px-3 py-2 font-mono text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={!submittable}>
              {save.isPending ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={save.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
            {save.isError && (
              <span className="text-xs text-destructive">{formatEditError(save.error)}</span>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function formatEditError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'That slug is already in use.';
    if (err.status === 422) return 'Invalid name or slug.';
    return formatApiError(err, `HTTP ${err.status}`);
  }
  return 'Save failed — see console.';
}

function ArchiveCustomerButton({ slug }: { slug: string }) {
  const { userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);

  const archive = useMutation({
    mutationFn: async () => archiveCustomer(slug, (await getIdToken() ?? '')),
    onSuccess: () => {
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['customers', slug, userKey] });
      qc.invalidateQueries({ queryKey: ['engagements', 'by-customer', slug, userKey] });
      void navigate({ to: '/customers' });
    },
  });

  if (!confirming) {
    return (
      <Button type="button" size="sm" variant="outline" onClick={() => setConfirming(true)}>
        Archive customer
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-brand-slate">
        Archive this customer + every engagement + run under it?
      </span>
      <Button
        type="button"
        size="sm"
        variant="destructive"
        disabled={archive.isPending}
        onClick={() => archive.mutate()}
      >
        {archive.isPending ? 'Archiving…' : 'Confirm'}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={archive.isPending}
        onClick={() => setConfirming(false)}
      >
        Cancel
      </Button>
      {archive.isError && (
        <span className="text-xs text-destructive">archive failed — see console</span>
      )}
    </div>
  );
}

function InviteCustomerCard({ slug }: { slug: string }) {
  const { getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [lastSentTo, setLastSentTo] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: async () => createInvitation(slug, email.trim(), (await getIdToken() ?? '')),
    onSuccess: (inv) => {
      setLastSentTo(inv.email);
      setEmail('');
      // Surface the new pending invite in the people list immediately.
      void queryClient.invalidateQueries({ queryKey: ['customers', slug, 'members'] });
    },
  });

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Invite a customer</CardTitle>
        <CardDescription>
          Sends a Clerk invitation link to the email. The recipient sets a password
          and lands on /portal scoped to this customer.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (email.trim().length === 0) return;
            send.mutate();
          }}
        >
          <label className="grid flex-1 gap-1 text-sm">
            <span className="text-brand-slate">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="customer@example.com"
              className="rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
            />
          </label>
          <Button type="submit" size="sm" disabled={send.isPending}>
            {send.isPending ? 'Sending…' : 'Send invitation'}
          </Button>
        </form>
        {send.isError && (
          <p className="mt-2 text-xs text-destructive">
            {formatApiError(send.error, 'Invitation failed')}
          </p>
        )}
        {lastSentTo && !send.isError && (
          <p className="mt-2 text-xs text-emerald-700">
            Invitation sent to <span className="font-medium">{lastSentTo}</span>.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function CustomerMembersTable({ slug }: { slug: string }) {
  const { userKey, getIdToken } = useAuth();
  const queryClient = useQueryClient();
  const members = useQuery({
    // userKey scopes the cache to the signed-in practitioner so a user
    // switch can't show the previous user's cached member list. The
    // invalidation sites below use the `['customers', slug, 'members']`
    // prefix, which still matches this user-scoped key.
    queryKey: ['customers', slug, 'members', userKey],
    queryFn: async () => listCustomerMembers(slug, (await getIdToken()) ?? ''),
  });

  if (members.isPending) {
    return <p className="text-xs text-brand-slate">Loading people…</p>;
  }
  if (members.isError) {
    return <ErrorCard title="Couldn't load people" error={members.error} />;
  }
  const items = members.data.items;
  if (items.length === 0) {
    return (
      <p className="text-xs text-brand-slate">No people yet. Send an invitation above.</p>
    );
  }
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead>Email</TableHead>
          <TableHead className="w-28">Status</TableHead>
          <TableHead className="w-40">When</TableHead>
          <TableHead className="w-20"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((m) => (
          <MemberRow
            key={m.invitation_id ?? m.user_id ?? m.email}
            member={m}
            slug={slug}
            onChanged={() => {
              void queryClient.invalidateQueries({ queryKey: ['customers', slug, 'members'] });
            }}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function MemberStatusBadge({ status }: { status: CustomerMemberOut['status'] }) {
  if (status === 'pending') {
    return (
      <Badge variant="outline" className="border-amber-500/40 text-amber-700">
        Pending
      </Badge>
    );
  }
  if (status === 'disabled') {
    return (
      <Badge variant="outline" className="border-destructive/40 text-destructive">
        Removed
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-emerald-600/40 text-emerald-700">
      Active
    </Badge>
  );
}

function MemberRow({
  member,
  slug,
  onChanged,
}: {
  member: CustomerMemberOut;
  slug: string;
  onChanged: () => void;
}) {
  const { getIdToken } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: async () => {
      // Only accepted users have a local row to remove; pending invites
      // are revoked separately (not yet wired — see INP-704 follow-up).
      if (!member.user_id) return;
      await deleteCustomerUser(slug, member.user_id, (await getIdToken()) ?? '');
    },
    onSuccess: () => {
      setConfirming(false);
      onChanged();
    },
  });

  const when =
    member.status === 'pending'
      ? member.invited_at
        ? `Invited ${formatDate(member.invited_at)}`
        : 'Invited'
      : member.joined_at
        ? formatDate(member.joined_at)
        : '—';

  return (
    <tr className="border-t border-border">
      <td className="px-3 py-2 truncate" title={member.email}>
        {member.email}
      </td>
      <td className="px-3 py-2">
        <MemberStatusBadge status={member.status} />
      </td>
      <td className="px-3 py-2 text-brand-slate">{when}</td>
      <td className="px-3 py-2 text-right">
        {member.status === 'active' ? (
          confirming ? (
            <div className="flex justify-end gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={remove.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
              >
                {remove.isPending ? 'Removing…' : 'Confirm'}
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setConfirming(true)}
            >
              Remove
            </Button>
          )
        ) : null}
        {remove.isError && (
          <p className="mt-1 text-right text-xs text-destructive">
            {remove.error instanceof ApiError && remove.error.status === 502
              ? 'Removed from DB but Clerk cleanup failed — delete the user in the Clerk dashboard.'
              : formatApiError(remove.error, 'Remove failed')}
          </p>
        )}
      </td>
    </tr>
  );
}
